import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthContext';

interface LocationState {
  email?: string;
}

/**
 * Email-verification page mounted at /confirm.
 *
 * The signup page navigates here with `state.email` so the user only
 * needs to type the 6-digit code Cognito mailed them. They can also
 * land here directly (e.g. clicking a link in the email) and type the
 * email manually.
 *
 * On success, navigates to /login so the user can sign in with their
 * now-confirmed account.
 */
export const ConfirmPage: React.FC = () => {
  const { confirm, isConfirming, confirmError, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const seedEmail = (location.state as LocationState | undefined)?.email ?? '';

  const [email, setEmail] = useState(seedEmail);
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  const emailInvalid = submitted && email.trim() === '';
  const codeInvalid = submitted && code.trim() === '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (email.trim() === '' || code.trim() === '') return;

    try {
      await confirm(email.trim(), code.trim());
      navigate('/login', {
        replace: true,
        state: { confirmed: true, email: email.trim() },
      });
    } catch {
      /* confirmError surfaces below */
    }
  };

  const handleResend = async () => {
    if (email.trim() === '') {
      setResendNotice('Enter your email first, then click resend.');
      return;
    }
    setResendNotice(null);
    await resendCode(email.trim());
    setResendNotice('A new code has been sent if an account exists for that email.');
  };

  return (
    <div
      data-testid="confirm-page"
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border rounded-lg shadow-sm p-6 space-y-4"
        aria-label="Confirm your email address"
        noValidate
      >
        <header>
          <h1 className="text-lg font-semibold">Verify your email</h1>
          <p className="text-sm text-gray-600">
            We emailed a 6-digit code to {email || 'your inbox'}. Paste it below to
            activate your account.
          </p>
        </header>

        <div className="space-y-1">
          <label htmlFor="email" className="block text-sm font-medium">Email</label>
          <input
            id="email" name="email" type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            aria-invalid={emailInvalid || undefined}
            disabled={isConfirming}
            className="block w-full border rounded px-2 py-1 text-sm"
          />
          {emailInvalid && <p className="text-xs text-red-600">Email is required</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="code" className="block text-sm font-medium">Verification code</label>
          <input
            id="code" name="code" type="text" inputMode="numeric"
            autoComplete="one-time-code" required maxLength={10}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))}
            aria-invalid={codeInvalid || undefined}
            disabled={isConfirming}
            className="block w-full border rounded px-2 py-1 text-sm tracking-widest font-mono"
          />
          {codeInvalid && <p className="text-xs text-red-600">Code is required</p>}
        </div>

        {confirmError && (
          <div role="alert" className="text-sm text-red-600">{confirmError}</div>
        )}
        {resendNotice && (
          <div role="status" className="text-sm text-blue-700">{resendNotice}</div>
        )}

        <button
          type="submit"
          disabled={isConfirming}
          className="w-full bg-blue-600 text-white text-sm py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConfirming ? 'Verifying…' : 'Verify and continue'}
        </button>

        <div className="flex items-center justify-between text-xs pt-2 border-t">
          <button
            type="button" onClick={handleResend}
            className="text-blue-600 hover:underline"
          >
            Resend code
          </button>
          <Link to="/login" className="text-gray-600 hover:underline">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ConfirmPage;
